/** Canonical stock exchange groups (aligned with trade filters). */
export type StockExchangeGroup = 'US Exchanges' | 'BIST' | 'London' | 'Other'

export const STOCK_EXCHANGE_GROUPS: { id: StockExchangeGroup; label: string; hint: string }[] = [
  { id: 'US Exchanges', label: 'US Exchanges', hint: 'NASDAQ, NYSE, NYSE Arca, …' },
  { id: 'BIST', label: 'BIST', hint: 'Borsa Istanbul' },
  { id: 'London', label: 'London', hint: 'London Stock Exchange (LSE)' },
  { id: 'Other', label: 'Other stock exchanges', hint: 'All other listed venues' },
]

export const FEE_RULE_ASSET_MARKETS = ['Stock', 'Crypto', 'Forex', 'Commodity'] as const
export type FeeRuleAssetMarket = (typeof FEE_RULE_ASSET_MARKETS)[number]

export const MARKET_ONLY_FEE_SCOPES: { market: FeeRuleAssetMarket; label: string }[] = [
  { market: 'Crypto', label: 'Crypto' },
  { market: 'Forex', label: 'Forex' },
  { market: 'Commodity', label: 'Commodity' },
]

export function canonicalizeStockExchangeGroup(raw: string | null | undefined): StockExchangeGroup {
  if (!raw) return 'Other'
  const trimmed = raw.trim()
  if (!trimmed) return 'Other'
  const upper = trimmed.toUpperCase()
  const upperNorm = upper.replace(/İ/g, 'I')

  const isNasdaq =
    upper === 'NASDAQ' ||
    upper === 'XNAS' ||
    upper.includes('NASDAQ STOCK') ||
    upper.includes('NASDAQ GLOBAL') ||
    upper.includes('NASDAQ CAPITAL') ||
    upper.includes('NASDAQ GS') ||
    upper.includes('NASDAQ GM')
  const isNyse =
    upper === 'NYSE' ||
    upper === 'XNYS' ||
    upper === 'XASE' ||
    upper.includes('NEW YORK STOCK EXCHANGE') ||
    upper.includes('NYSE AMERICAN') ||
    upper.includes('NYSE ARCA')

  if (isNasdaq || isNyse) return 'US Exchanges'

  const isBist =
    upper === 'BIST' ||
    upperNorm.includes('BORSA ISTANBUL') ||
    upper === 'XIST'
  if (isBist) return 'BIST'

  const isLondon =
    upper === 'LSE' ||
    upper === 'XLON' ||
    upper.includes('LONDON STOCK EXCHANGE')
  if (isLondon) return 'London'

  return 'Other'
}

export function buildFeeScopeKey(
  market: string | null | undefined,
  exchange: string | null | undefined
): string | null {
  if (!market) return null
  if (market === 'Stock') {
    return `Stock:${canonicalizeStockExchangeGroup(exchange)}`
  }
  if (market === 'Crypto' || market === 'Forex' || market === 'Commodity') {
    return market
  }
  return null
}

export const ALL_FEE_SCOPE_KEYS: string[] = [
  ...STOCK_EXCHANGE_GROUPS.map((group) => `Stock:${group.id}`),
  ...MARKET_ONLY_FEE_SCOPES.map((entry) => entry.market),
]

export function isFeeScopeKey(value: string): boolean {
  return ALL_FEE_SCOPE_KEYS.includes(value)
}
